import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Search,
  UserPlus,
  UserMinus,
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle,
  Loader2,
  Link as LinkIcon,
  Upload,
  Mail,
  Plus,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Input,
  Checkbox,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Modal,
  ModalFooter,
  Textarea,
} from '@azyntor-labs/nexus-ui';
import { examsApi, studentsApi, attemptsApi } from '@/api/endpoints';
import { useDebounce } from '@/hooks/useDebounce';
import { EnrollmentLinkManager } from '@/components/enrollment/EnrollmentLinkManager';
import { CSVImportUploader } from '@/components/enrollment/CSVImportUploader';
import { GroupEnrollment } from '@/components/enrollment/GroupEnrollment';
import { EmailInvitationSender } from '@/components/enrollment/EmailInvitationSender';
import { format } from 'date-fns';
import { formatExamDateShort } from '@/utils';
import { toast } from '@/utils/toast';

interface StudentSearchResult {
  id: string;
  email: string;
  username: string;
  name: string;
}

interface EnrolledStudent {
  id: string;
  student_id: string;
  student_name?: string;
  student_username?: string;
  student_email: string;
  enrolled_at: string;
  enrolled_by: string;
  is_active: boolean;
  enrollment_source?: string;
  enrollment_metadata?: Record<string, any>;
  campaign_id?: string;
  extra_attempts?: number;
}

export const ExamEnrollmentPage = () => {
  const { examId } = useParams<{ examId: string }>();
  useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>('available');
  const [availableSearch, setAvailableSearch] = useState('');
  const [enrolledSearch, setEnrolledSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  // Grant extra attempts — per-row modal state
  const [grantTarget, setGrantTarget] = useState<EnrolledStudent | null>(null);
  const [grantDelta, setGrantDelta] = useState<number>(1);
  const [grantReason, setGrantReason] = useState<string>('');

  // Debounce search for API optimization (300ms delay)
  const debouncedSearch = useDebounce(availableSearch, 300);

  // Fetch exam details
  const { data: examResponse } = useQuery({
    queryKey: ['exam', examId],
    queryFn: () => examId ? examsApi.get(examId) : Promise.resolve(null),
    enabled: !!examId,
  });

  const exam = examResponse?.data?.data;

  // Fetch students with autocomplete search (server-side)
  const { data: studentsSearchResponse, isLoading: isSearching } = useQuery({
    queryKey: ['students-search', debouncedSearch, page, examId],
    queryFn: () => studentsApi.search({
      q: debouncedSearch,
      page,
      per_page: 20,
      exclude_exam_id: examId,
    }),
    enabled: debouncedSearch.length >= 2, // Only search with 2+ characters
    staleTime: 30000,
  });

  const searchResults: StudentSearchResult[] = studentsSearchResponse?.data?.data?.students || [];
  const totalAvailable = studentsSearchResponse?.data?.data?.total || 0;

  // Fetch enrolled students
  const { data: enrolledResponse, isLoading: isLoadingEnrolled, refetch: refetchEnrolled } = useQuery({
    queryKey: ['enrollments', examId],
    queryFn: () => examId ? examsApi.getEnrolledStudents(examId) : Promise.resolve(null),
    enabled: !!examId,
  });

  const enrolledStudents: EnrolledStudent[] = Array.isArray(enrolledResponse?.data?.data)
    ? enrolledResponse.data.data
    : [];

  // Filter enrolled students by search (client-side since list is small)
  const filteredEnrolledStudents = enrolledStudents.filter((enrollment) => {
    if (!enrolledSearch) return true;
    const searchLower = enrolledSearch.toLowerCase();
    return (
      (enrollment.student_name && enrollment.student_name.toLowerCase().includes(searchLower)) ||
      (enrollment.student_username && enrollment.student_username.toLowerCase().includes(searchLower)) ||
      enrollment.student_email.toLowerCase().includes(searchLower)
    );
  });

  // Helper: Check if student is already enrolled (safeguard)
  const isStudentEnrolled = (studentId: string) => {
    return enrolledStudents.some(e => e.student_id === studentId);
  };

  // Enroll students mutation
  const enrollMutation = useMutation({
    mutationFn: (studentIds: string[]) => {
      if (!examId) throw new Error('Exam ID is required');
      return examsApi.enrollStudents(examId, studentIds);
    },
    onSuccess: (response) => {
      const enrolledCount = response.data?.data?.enrolled_count || selectedStudentIds.length;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      queryClient.invalidateQueries({ queryKey: ['students-search', debouncedSearch, page, examId] }); // Refetch search results
      refetchEnrolled(); // Refetch enrolled students

      toast.success(`${enrolledCount} student(s) enrolled successfully - showing next ${enrolledCount} students`);
      setSelectedStudentIds([]); // Clear selection only
      // Keep search term - results will auto-refresh with next batch
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to enroll students';
      toast.error(message);
    },
  });

  // Unenroll student mutation
  const unenrollMutation = useMutation({
    mutationFn: ({ studentId }: { studentId: string }) => {
      if (!examId) throw new Error('Exam ID is required');
      return examsApi.unenrollStudent(examId, studentId);
    },
    onSuccess: () => {
      refetchEnrolled();
      toast.success('Student unenrolled successfully');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to unenroll student';
      toast.error(message);
    },
  });

  // Grant extra attempts mutation — adds N attempts on top of exam.max_attempts for this enrollment.
  const grantAttemptsMutation = useMutation({
    mutationFn: ({ studentId, delta, reason }: { studentId: string; delta: number; reason: string }) => {
      if (!examId) throw new Error('Exam ID is required');
      return attemptsApi.grantExtraAttempts(examId, studentId, delta, reason);
    },
    onSuccess: (res) => {
      const total = res.data?.data?.extra_attempts ?? 0;
      toast.success(`Granted. Student now has ${total} extra attempt(s).`);
      refetchEnrolled();
      setGrantTarget(null);
      setGrantDelta(1);
      setGrantReason('');
    },
    onError: (err: unknown) => {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(anyErr?.response?.data?.message || anyErr?.message || 'Failed to grant attempts');
    },
  });

  // Handle student selection
  const handleToggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Handle select all (on current page)
  const handleSelectAll = () => {
    if (selectedStudentIds.length === searchResults.length && searchResults.length > 0) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(searchResults.map((s) => s.id));
    }
  };

  // Handle enroll selected
  const handleEnrollSelected = () => {
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    enrollMutation.mutate(selectedStudentIds);
  };

  // Handle unenroll
  const handleUnenroll = (studentId: string) => {
    unenrollMutation.mutate({ studentId });
  };

  return (
    <div className="min-h-screen bg-[#0d0d12] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/teacher/exams"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Exams
          </Link>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Student Enrollment</h1>
              {exam && (
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="font-medium text-gray-300">{exam.title}</span>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {exam.start_time && formatExamDateShort(exam.start_time)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {exam.duration_minutes || exam.duration} minutes
                  </div>
                  <Badge variant={exam.status === 'active' ? 'success' : 'secondary'}>
                    {exam.status}
                  </Badge>
                </div>
              )}
            </div>

            <div className="text-right">
              <div className="text-sm text-gray-400">Total Enrolled</div>
              <div className="text-3xl font-bold text-teal-400">
                {enrolledStudents.length}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Card className="bg-[#12121a]/80 border-white/[0.06] backdrop-blur-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="available" className="w-full">
            <TabsList className="w-full border-b border-white/[0.06] bg-transparent p-0">
              <TabsTrigger
                value="available"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Available Students {debouncedSearch.length >= 2 && `(${totalAvailable})`}
              </TabsTrigger>
              <TabsTrigger
                value="enrolled"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <Users className="w-4 h-4 mr-2" />
                Enrolled Students ({enrolledStudents.length})
              </TabsTrigger>
              <TabsTrigger
                value="links"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Magic Links
              </TabsTrigger>
              <TabsTrigger
                value="import"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <Upload className="w-4 h-4 mr-2" />
                CSV Import
              </TabsTrigger>
              <TabsTrigger
                value="groups"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <Users className="w-4 h-4 mr-2" />
                Group Enrollment
              </TabsTrigger>
              <TabsTrigger
                value="email"
                className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:text-teal-400"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Invitations
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Available Students */}
            <TabsContent value="available" className="mt-0 p-6">
              <div className="space-y-4">
                {/* Search and Actions Bar */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 max-w-md">
                    <Input
                      placeholder="Search by name, email, or username (min 2 chars)..."
                      value={availableSearch}
                      onChange={(e) => {
                        setAvailableSearch(e.target.value);
                        setPage(1); // Reset to page 1 on new search
                        setSelectedStudentIds([]); // Clear selection
                      }}
                      leftIcon={isSearching ? <Loader2 className="w-4 h-4 animate-spin text-teal-400" /> : <Search className="w-4 h-4" />}
                      className="bg-[#0d0d12] border-white/[0.06] text-white placeholder:text-gray-500"
                    />
                    {debouncedSearch.length > 0 && debouncedSearch.length < 2 && (
                      <p className="text-xs text-amber-400 mt-1">Type at least 2 characters to search</p>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {selectedStudentIds.length > 0 && (
                      <span className="text-sm text-gray-400">
                        {selectedStudentIds.length} selected
                      </span>
                    )}
                    <Button
                      onClick={handleEnrollSelected}
                      disabled={selectedStudentIds.length === 0 || enrollMutation.isPending}
                      variant="primary"
                      size="sm"
                      leftIcon={enrollMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                      className="bg-teal-500 hover:bg-teal-600 text-white"
                    >
                      {enrollMutation.isPending ? 'Enrolling...' : `Enroll Selected (${selectedStudentIds.length})`}
                    </Button>
                  </div>
                </div>

                {/* Search Results */}
                {debouncedSearch.length < 2 ? (
                  <div className="text-center py-16 border border-white/[0.06] rounded-lg bg-[#0d0d12]/50">
                    <Search className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400 text-lg mb-2">Search for Students to Enroll</p>
                    <p className="text-gray-500 text-sm">
                      Type at least 2 characters to search across {enrolledStudents.length > 0 ? 'available' : 'all'} students
                    </p>
                    <p className="text-teal-400 text-sm mt-2">
                      Optimized to search across 5,000+ students instantly
                    </p>
                  </div>
                ) : isSearching ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto text-teal-400 animate-spin mb-3" />
                    <p className="text-gray-400">Searching students...</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-12 border border-white/[0.06] rounded-lg bg-[#0d0d12]/50">
                    <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">No students found matching "{debouncedSearch}"</p>
                    <p className="text-gray-500 text-sm mt-2">Try a different search term</p>
                  </div>
                ) : (
                  <>
                    <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                      <Table>
                        <TableHead>
                          <TableRow className="bg-[#12121a]/50 border-b border-white/[0.06]">
                            <TableHeader className="w-12">
                              <Checkbox
                                checked={
                                  selectedStudentIds.length === searchResults.length &&
                                  searchResults.length > 0
                                }
                                onChange={handleSelectAll}
                              />
                            </TableHeader>
                            <TableHeader className="text-gray-300">Student Name</TableHeader>
                            <TableHeader className="text-gray-300">Email</TableHeader>
                            <TableHeader className="text-gray-300">Username</TableHeader>
                            <TableHeader className="text-right text-gray-300">Action</TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {searchResults.map((student) => {
                            const alreadyEnrolled = isStudentEnrolled(student.id);
                            return (
                            <TableRow key={student.id} className="border-b border-white/[0.06] hover:bg-teal-500/5">
                              <TableCell>
                                <Checkbox
                                  checked={selectedStudentIds.includes(student.id) || alreadyEnrolled}
                                  onChange={() => !alreadyEnrolled && handleToggleStudent(student.id)}
                                  disabled={alreadyEnrolled}
                                  className={alreadyEnrolled ? 'opacity-50 cursor-not-allowed' : ''}
                                />
                              </TableCell>
                              <TableCell className="font-medium text-white">
                                {student.name || '-'}
                              </TableCell>
                              <TableCell className="text-gray-400">{student.email}</TableCell>
                              <TableCell className="text-gray-400">
                                {student.username || '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {isStudentEnrolled(student.id) ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                    <CheckCircle className="w-3 h-3" />
                                    Already Enrolled
                                  </span>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => enrollMutation.mutate([student.id])}
                                    disabled={enrollMutation.isPending}
                                    leftIcon={<UserPlus className="w-3 h-3" />}
                                    className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10"
                                  >
                                    Enroll
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination Info */}
                    {totalAvailable > 20 && (
                      <div className="flex items-center justify-between text-sm text-gray-400">
                        <span>
                          Showing {searchResults.length} of {totalAvailable} results
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="text-gray-400 hover:text-teal-400"
                          >
                            Previous
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            disabled={searchResults.length < 20}
                            className="text-gray-400 hover:text-teal-400"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* Tab 2: Enrolled Students */}
            <TabsContent value="enrolled" className="mt-0 p-6">
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 max-w-md">
                    <Input
                      placeholder="Search enrolled students..."
                      value={enrolledSearch}
                      onChange={(e) => setEnrolledSearch(e.target.value)}
                      leftIcon={<Search className="w-4 h-4" />}
                      className="bg-[#0d0d12] border-white/[0.06] text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div className="text-sm text-gray-400">
                    Total: {enrolledStudents.length} student(s) enrolled
                  </div>
                </div>

                {/* Enrolled Students Table */}
                {isLoadingEnrolled ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto text-teal-400 animate-spin mb-3" />
                    <p className="text-gray-400">Loading enrolled students...</p>
                  </div>
                ) : filteredEnrolledStudents.length === 0 ? (
                  <div className="text-center py-16 border border-white/[0.06] rounded-lg bg-[#0d0d12]/50">
                    <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400 text-lg mb-2">
                      {enrolledSearch ? 'No enrolled students found matching your search' : 'No students enrolled yet'}
                    </p>
                    {!enrolledSearch && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setActiveTab('available')}
                        className="mt-4 bg-teal-500 hover:bg-teal-600"
                        leftIcon={<UserPlus className="w-4 h-4" />}
                      >
                        Enroll Students
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                    <Table>
                      <TableHead>
                        <TableRow className="bg-[#12121a]/50 border-b border-white/[0.06]">
                          <TableHeader className="text-gray-300">Student</TableHeader>
                          <TableHeader className="text-gray-300">Username</TableHeader>
                          <TableHeader className="text-gray-300">Email</TableHeader>
                          <TableHeader className="text-gray-300">Source</TableHeader>
                          <TableHeader className="text-gray-300">Enrolled Date</TableHeader>
                          <TableHeader className="text-gray-300">Status</TableHeader>
                          <TableHeader className="text-right text-gray-300">Actions</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEnrolledStudents.map((enrollment) => (
                          <TableRow key={enrollment.id} className="border-b border-white/[0.06] hover:bg-teal-500/5">
                            <TableCell className="font-medium text-white">
                              {enrollment.student_name || '-'}
                            </TableCell>
                            <TableCell className="text-gray-400">
                              {enrollment.student_username || '-'}
                            </TableCell>
                            <TableCell className="text-gray-400">
                              {enrollment.student_email}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                {enrollment.enrollment_source?.replace('_', ' ') || 'manual'}
                              </span>
                            </TableCell>
                            <TableCell className="text-gray-400">
                              {format(new Date(enrollment.enrolled_at), 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {enrollment.is_active ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                  <CheckCircle className="w-3 h-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">
                                  Inactive
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex items-center gap-2 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setGrantTarget(enrollment);
                                    setGrantDelta(1);
                                    setGrantReason('');
                                  }}
                                  leftIcon={<Plus className="w-3 h-3" />}
                                  className="text-teal-300 hover:text-teal-200 hover:bg-teal-500/10"
                                  title={
                                    enrollment.extra_attempts && enrollment.extra_attempts > 0
                                      ? `Currently +${enrollment.extra_attempts}`
                                      : 'Grant extra attempts'
                                  }
                                >
                                  Grant{enrollment.extra_attempts ? ` (+${enrollment.extra_attempts})` : ''}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnenroll(enrollment.student_id)}
                                  disabled={unenrollMutation.isPending}
                                  leftIcon={<UserMinus className="w-3 h-3" />}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  Remove
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab 3: Magic Links */}
            <TabsContent value="links" className="mt-0 p-6">
              <EnrollmentLinkManager examId={examId || ''} />
            </TabsContent>

            {/* Tab 4: CSV Import */}
            <TabsContent value="import" className="mt-0 p-6">
              <CSVImportUploader examId={examId || ''} />
            </TabsContent>

            {/* Tab 5: Group Enrollment */}
            <TabsContent value="groups" className="mt-0 p-6">
              <GroupEnrollment examId={examId || ''} />
            </TabsContent>

            {/* Tab 6: Email Invitations */}
            <TabsContent value="email" className="mt-0 p-6">
              <EmailInvitationSender examId={examId || ''} examTitle={exam?.title || 'Exam'} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <Modal
        isOpen={!!grantTarget}
        onClose={() => {
          if (!grantAttemptsMutation.isPending) {
            setGrantTarget(null);
            setGrantDelta(1);
            setGrantReason('');
          }
        }}
        title="Grant extra attempts"
        subtitle={
          grantTarget
            ? `Grant additional attempts to ${grantTarget.student_name || grantTarget.student_email}. These stack on top of the exam's max attempts (${exam?.max_attempts ?? '—'}).`
            : ''
        }
      >
        <div className="space-y-3">
          <Input
            label="Extra attempts to add"
            type="number"
            min={1}
            max={10}
            value={String(grantDelta)}
            onChange={(e) => setGrantDelta(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
          />
          <Textarea
            label="Reason (required)"
            placeholder="e.g. Student's session was terminated due to network issue"
            value={grantReason}
            onChange={(e) => setGrantReason(e.target.value)}
            rows={3}
          />
          {grantTarget && grantTarget.extra_attempts ? (
            <p className="text-xs text-white/50">
              Current extra attempts for this student: <span className="text-white">+{grantTarget.extra_attempts}</span>
            </p>
          ) : null}
          <p className="text-xs text-white/40">
            The reason + your user ID will be recorded in audit_logs.
          </p>
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setGrantTarget(null);
              setGrantDelta(1);
              setGrantReason('');
            }}
            disabled={grantAttemptsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (!grantTarget) return;
              grantAttemptsMutation.mutate({
                studentId: grantTarget.student_id,
                delta: grantDelta,
                reason: grantReason.trim(),
              });
            }}
            disabled={!grantReason.trim() || grantDelta < 1 || grantAttemptsMutation.isPending}
            isLoading={grantAttemptsMutation.isPending}
          >
            Grant +{grantDelta}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
